import {
  Center,
  Group,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Text,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import {
  IconEdit,
  IconExternalLink,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react"
import { Button, ActionIcon, SectionHeader, Loader } from "@tfg/ui"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  FindContactsByCreatedByIdResponseDto,
  useFindContactsByCreatedById,
} from "~/gen/api"
import { defaultUpdatePermissions } from "~/pages/Brand/Suppliers/SuppliersRepository/utils/useFormFieldConfig"
import { DeleteContactModal } from "~/pages/SupplierPortal/Contacts/DeleteContactModal.tsx"
import useContactForm, {
  contactToSupplierMapping,
} from "~/pages/SupplierPortal/Contacts/hooks/useContactForm"
import { ContactDrawer } from "~/pages/SupplierPortal/TraceabilityRequests/components/ContactDrawer"
import { useAuth } from "~/store/auth-context.tsx"

export const SupplierContacts = () => {
  const { t } = useTranslation(["supplier", "organizationSettings"])
  const { isAdmin, user } = useAuth()

  const {
    data: contacts,
    isPending: isLoading,
    isError,
  } = useFindContactsByCreatedById(user?.organization?.id ?? "", {
    query: {
      enabled: !!user?.organization?.id,
    },
  })
  const { contact, setContact, reset, handleContact } = useContactForm({
    organizationId: user?.organization?.id ?? "",
  })
  const [isModalOpened, modalActions] = useDisclosure(false)
  const [openedDelete, { open: openDelete, close: closeDelete }] =
    useDisclosure()
  const [deletedContact, setDeletedContact] =
    useState<FindContactsByCreatedByIdResponseDto>()

  const isUpdate =
    ("contactId" in contact && contact.contactId !== "") ||
    ("supplierId" in contact && contact.supplierId !== "")

  const readonly = useMemo<boolean>(() => {
    const selectedContact = contacts?.data.find(
      (c) => c.user.email === contact.email
    )

    if (!selectedContact) {
      return false
    }

    return selectedContact.readonly
  }, [contacts, contact.email])

  if (isLoading) {
    return <Loader fullPage />
  } else if (isError) {
    return (
      <Center h="100%">
        <Text c="red" fz="xl">
          {t("request.errors.generic")}
        </Text>
      </Center>
    )
  }
  return (
    <>
      <SectionHeader
        mainCta={
          <Button
            icon={<IconPlus />}
            onClick={() => {
              reset()
              modalActions.open()
            }}
          >
            {t("contacts.buttons.add")}
          </Button>
        }
      />
      <Table verticalSpacing="sm" highlightOnHover>
        <TableThead>
          <TableTr>
            {isAdmin ? (
              <TableTh>
                <Text c="violet">{t("contacts.table.headers.id")}</Text>
              </TableTh>
            ) : null}
            <TableTh>{t("contacts.table.headers.name")}</TableTh>
            <TableTh>{t("contacts.table.headers.email")}</TableTh>
            <TableTh>{t("contacts.table.headers.actions")}</TableTh>
          </TableTr>
        </TableThead>
        <TableTbody>
          {contacts?.data?.map((contact) => (
            <TableTr key={contact.id}>
              {isAdmin ? (
                <TableTd>
                  <Text c="violet">{contact.id}</Text>
                </TableTd>
              ) : null}
              <TableTd>
                {contact.user.name || contact.user.organization.name}
              </TableTd>
              <TableTd>{contact.user.email}</TableTd>
              <TableTd style={{ minWidth: "240px" }}>
                <Group wrap="nowrap">
                  <ActionIcon
                    icon={
                      contact.readonly ? <IconExternalLink /> : <IconEdit />
                    }
                    onClick={() => {
                      reset()
                      setContact(contactToSupplierMapping(contact))
                      modalActions.open()
                    }}
                  />
                  {!contact.readonly ? (
                    <ActionIcon
                      icon={<IconTrash />}
                      onClick={() => {
                        setDeletedContact(contact)
                        openDelete()
                      }}
                    />
                  ) : null}
                </Group>
              </TableTd>
            </TableTr>
          ))}
        </TableTbody>
      </Table>
      {isModalOpened && (
        <ContactDrawer
          opened={isModalOpened}
          setModalOpened={modalActions}
          supplierMapping={contact}
          handleSupplierMapping={handleContact}
          isUpdate={isUpdate}
          showAnswerOnBehalfPolicy
          updatePermissions={{ ...defaultUpdatePermissions, readonly }}
        />
      )}
      {deletedContact && (
        <DeleteContactModal
          isOpen={openedDelete}
          onClose={() => closeDelete()}
          contact={deletedContact}
          user={user}
        />
      )}
    </>
  )
}
